import { useCallback, useSyncExternalStore } from "react";

// Ein Thema ergänzen:
//   1) hier einen Eintrag anhängen
//   2) in index.css einen `[data-theme="<id>"] { … }`-Block dazu
// Mehr ist es nicht — die Vorschau in den Einstellungen setzt dasselbe
// Attribut auf eine Kachel und zeigt damit die echten Farben, nicht
// nachgebaute.
export type ThemeOption = {
  id: string;
  label: string;
  /** Gruppenüberschrift in den Einstellungen. */
  gruppe: string;
  /** Helles Thema — die Liste weist darauf hin, sonst wählt man es aus
   *  Versehen und sitzt im Weißen. */
  hell?: boolean;
};

export const THEME_OPTIONS: ThemeOption[] = [
  { id: "catppuccin-mocha",     label: "Mocha",      gruppe: "Catppuccin" },
  { id: "catppuccin-macchiato", label: "Macchiato",  gruppe: "Catppuccin" },
  { id: "catppuccin-frappe",    label: "Frappé",     gruppe: "Catppuccin" },
  { id: "catppuccin-latte",     label: "Latte",      gruppe: "Catppuccin", hell: true },
];

// Themen, die es einmal gab. Wer eines davon eingestellt hatte, landet beim
// nächsten Laden auf Mocha, statt auf einer Seite ohne Farben zu sitzen.
//
// Sie konnten gehen, weil sie nie dazugehört haben: keines von ihnen setzte
// auch nur eine der 73 Palettenmarken (--ctp-*). Jede Regel, die mit der
// Palette rechnet, fiel dort still auf Mocha zurück — in Gruvbox waren das
// kalte blaugraue Linien in einem warmen Braun —, und ihr --border war
// heller als ihre hellste Fläche, also umgab jede Karte ein leuchtendes
// Gitter. Sie zu reparieren hätte geheissen, dreimal eine ganze Palette
// nachzubauen.
const ABGELEGT = new Set(["tokyo-night", "gruvbox", "serenity"]);

/** Gibt es das Thema noch? */
export function themeBekannt(id: string): boolean {
  return THEME_OPTIONS.some((t) => t.id === id);
}

export type Theme = string;

const KEY = "home.theme";
const DEFAULT: Theme = THEME_OPTIONS[0].id;

/** Returns the currently-stored cookie value for `home.theme`, or null. */
function readThemeCookie(): Theme | null {
  if (typeof document === "undefined") return null;
  // Cookies are `; `-separated key=value pairs; pull ours out without
  // pulling in a parser.
  const raw = document.cookie || "";
  for (const part of raw.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const k = part.slice(0, eq).trim();
    if (k !== KEY) continue;
    try {
      const v = decodeURIComponent(part.slice(eq + 1).trim());
      if (THEME_OPTIONS.some((o) => o.id === v)) return v;
    } catch { /* malformed cookie — ignore */ }
    return null;
  }
  return null;
}

// Preference order: cookie → localStorage → default.
//
// Cookie wins so a choice made on a sibling subdomain (the static-site
// login page on `sites.*`) propagates back to the main app on the next
// load — without it, each origin keeps its own localStorage and the two
// drift apart.
function read(): Theme {
  const fromCookie = readThemeCookie();
  // Ein abgelegtes Thema wird still zu Mocha. Der Keks hielt die Wahl auch
  // über die Nachbardomäne hinweg fest; ohne diese Zeile sässe man nach dem
  // Entfernen auf einer Seite, deren Farben es nicht mehr gibt.
  if (fromCookie && themeBekannt(fromCookie)) return fromCookie;
  if (fromCookie && ABGELEGT.has(fromCookie)) return DEFAULT;
  if (fromCookie) return fromCookie;
  try {
    const t = localStorage.getItem(KEY);
    if (t && themeBekannt(t)) return t;
  } catch { /* swallow */ }
  return DEFAULT;
}

// Module-global state + subscriber list. A single source of truth so every
// `useTheme()` consumer sees changes synchronously — without it, each
// component holds its own copy and the iframe in EmailBody silently keeps
// the previous theme's palette.
let currentTheme: Theme = read();
const subs = new Set<() => void>();

function notify() { for (const s of subs) s(); }

export function applyTheme(t: Theme) {
  currentTheme = t;
  document.documentElement.dataset.theme = t;
  try { localStorage.setItem(KEY, t); } catch { /* swallow */ }
  writeThemeCookie(t);
  notify();
}

// Mirror the theme into a cookie scoped to the parent domain so that
// sibling subdomains (notably `sites.*` for the protected static-site login
// page) can pick up the user's choice. localStorage is per-origin so it
// can't bridge the gap on its own. For localhost / IP literals / single-label
// hosts the Domain attribute is omitted (cookie stays origin-scoped).
function writeThemeCookie(t: Theme) {
  if (typeof document === "undefined") return;
  try {
    const host = location.hostname;
    let domain = "";
    const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(host);
    if (host !== "localhost" && !isIp) {
      const parts = host.split(".");
      if (parts.length >= 2) domain = "; Domain=." + parts.slice(-2).join(".");
    }
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${KEY}=${encodeURIComponent(t)}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
  } catch { /* swallow */ }
}

/** Voller Name inklusive Gruppe („Catppuccin Mocha"). In den Einstellungen
 *  steht die Gruppe als Überschrift, dort reicht `label` allein. */
export function themeLabel(id: Theme): string {
  const o = THEME_OPTIONS.find((x) => x.id === id);
  return o ? `${o.gruppe === "Weitere" ? "" : o.gruppe + " "}${o.label}` : id;
}

// Apply once at module load so the first paint matches the persisted choice.
applyTheme(currentTheme);

function subscribe(cb: () => void): () => void {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

function getSnapshot(): Theme { return currentTheme; }

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setTheme = useCallback((t: Theme) => applyTheme(t), []);
  return { theme, setTheme, options: THEME_OPTIONS };
}
